import { Link } from "react-router-dom";
import { SkedMark } from "@/components/SkedMark";
import { usePageMeta } from "@/hooks/usePageMeta";
import { buildSkedHref } from "@shared/leadgen/cta-rules";

const LANDING_MARQUEE_LOGO_HREF = buildSkedHref({
  medium: "landing",
  campaign: "marquee",
  content: "logo",
});
const LANDING_MARQUEE_LEARN_MORE_HREF = buildSkedHref({
  medium: "landing",
  campaign: "marquee",
  content: "learn_more_link",
});
const LANDING_MID_EXPLORE_HREF = buildSkedHref({
  medium: "landing",
  campaign: "mid_cta",
  content: "explore_button",
});
const LANDING_FINAL_CTA_PRICING_HREF = buildSkedHref({
  medium: "landing",
  campaign: "final_cta",
  content: "pricing_link",
  path: "/pricing",
});
const LANDING_SITE_FOOTER_HREF = buildSkedHref({
  medium: "landing",
  campaign: "site_footer",
});

// Ported from the 2026-04 design handoff
// (`docs/design-system/reference/homepage.html`).
// - Pink #ED207C is the PRIMARY CTA everywhere.
// - Purple #4F31FF is the SECONDARY — links + the accent word in Gotham display headlines.
// - Gotham SSm Black 900 for marketing display headlines.
// - Quarto italic for editorial eyebrows, pull-quotes, and single-word accents.
// - Three "stages": white page → peach (About-Sked) → lavender wash (UTM section) → ink (final CTA band).
// - Cards and metrics use subtle neutral-100 borders, not the rings pattern from the app shell.

export function LandingPage() {
  usePageMeta({
    title: "Traffic Snapshot — the social-first GA4 read, free from Sked Social",
    description:
      "Connect GA4, get a social-first report in under a minute — plus a UTM Health score that flags the broken attribution costing you campaigns. Free, from Sked Social.",
  });
  return (
    <div className="min-h-dvh bg-white text-ink antialiased">
      <SiteNav />
      <Hero />
      <Marquee />
      <AboutSked />
      <ThreeReads />
      <UtmHealthStage />
      <FinalCtaBand />
      <SiteFooter />
    </div>
  );
}

function SiteNav() {
  return (
    <nav className="sticky top-0 z-20 border-b border-neutral-950/5 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-[1200px] items-center gap-9 px-8 py-4">
        <Link to="/" className="flex items-center gap-3 text-ink no-underline">
          <SkedMark className="h-10 w-10" aria-hidden="true" />
          <span className="leading-[1.05]">
            <span className="block text-xs font-bold tracking-tight text-neutral-500">
              Sked Social
            </span>
            <span className="block font-display text-lg font-[900] tracking-tight text-ink">
              Traffic Snapshot
            </span>
          </span>
        </Link>
        <div className="hidden items-center gap-7 text-sm font-medium text-neutral-700 md:flex">
          <a className="hover:text-purple-700" href="#cuts">
            Features
          </a>
          <a className="hover:text-purple-700" href="#health">
            UTM Health
          </a>
        </div>
        <div className="flex-1" />
        <Link
          to="/utm-health-check"
          className="hidden text-sm font-medium text-neutral-700 hover:text-purple-700 md:inline"
        >
          Audit your UTMs
        </Link>
        <Link to="/app" className="ts-cta">
          Get free report <span className="ts-cta-arrow">→</span>
        </Link>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header
      className="relative overflow-hidden bg-white py-16 pb-22 md:pb-24"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 110% 20%, #EEE9FF 0%, transparent 55%), radial-gradient(900px 420px at -10% 90%, #FDF3E8 0%, transparent 60%), #fff",
      }}
    >
      <div className="mx-auto grid max-w-[1200px] items-center gap-14 px-8 md:grid-cols-[1.1fr_0.9fr]">
        <div>
          {/* SKE-3438 item 3 — eyebrow now sells "free + 1 min" instead
           *  of the editorial "A report, not another dashboard." line.
           *  The headline can carry the brand voice; the eyebrow's job
           *  is to address the cold visitor's first two questions
           *  (cost + time-to-value) before they read anything else. */}
          <p className="font-serif italic text-lg font-medium text-purple-700">
            Free Google Analytics audit · 1-minute setup
          </p>
          <h1 className="mt-[18px] font-display text-[64px] font-[900] leading-[1] tracking-[-0.03em] text-ink md:text-[76px]">
            See what social{" "}
            <span className="font-serif italic font-semibold tracking-[-0.015em] text-purple-500">
              actually
            </span>{" "}
            drives on-site.
          </h1>
          {/* SKE-3438 items 1 + 4 collapsed into a single body block.
           *  The eyebrow already carries "free + 1 minute"; the H1
           *  carries the brand voice. This paragraph carries the
           *  differentiator (why not just use GA4?) AND the trust
           *  signal (who's behind this) in one read so the eye gets
           *  to the CTA quickly. Earlier draft had 5 separate text
           *  blocks — too many voices competing for scan attention.
           *  Sked voice: plain, specific, numbers over superlatives. */}
          <p className="mt-6 max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
            Connect GA4. One minute later, a social-first report — with a UTM Health score that
            flags the broken attribution costing you campaigns.
          </p>
          {/* SKE-3438 item 2 — primary CTA stays prominent; the
           *  "Audit UTMs without signing in" secondary is a small link
           *  BELOW the primary so the click decision is unambiguous.
           *  The privacy chip (item 5) sits inline next to the primary
           *  so it's read on the same visual scan as the CTA. */}
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to="/app" className="ts-cta ts-cta-lg">
              Get free report <span className="ts-cta-arrow">→</span>
            </Link>
            {/* SKE-3438 item 5 — promote the privacy promise from a
             *  near-invisible footnote to a small chip next to the
             *  CTA. 10× the visual weight of the old footnote, which
             *  was the conversion-blocker for privacy-conscious
             *  marketers. Wording kept literally accurate — GA4 data
             *  isn't persisted row-level; a derived report is cached
             *  briefly for performance. */}
            <span className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700">
              <i className="fa-regular fa-shield-check text-[11px]" aria-hidden="true" />
              Read-only · No data storage
            </span>
          </div>
          <Link
            to="/utm-health-check"
            className="ts-link mt-3 inline-flex items-center text-sm font-medium text-neutral-600"
          >
            Audit UTMs without signing in <span className="ts-link-arrow">→</span>
          </Link>
        </div>
        <div className="relative">
          <HeroReportCard />
        </div>
      </div>
    </header>
  );
}

function HeroReportCard() {
  return (
    <div className="relative rounded-[20px] border border-neutral-950/5 bg-white p-7 shadow-[0_28px_60px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
      <div className="mb-[18px] flex items-start justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
            Last 30 days · vs prior
          </p>
          <p className="mt-1 font-display text-xl font-bold tracking-[-0.01em] text-ink">
            Social overview
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1.5 text-xs font-bold text-green-700">
          ▲ 18.4%
        </span>
      </div>
      <dl className="mb-4 grid grid-cols-3 gap-4 border-y border-neutral-100 py-[10px] pb-[18px]">
        {[
          { label: "Sessions", value: "48,213", delta: "+12%" },
          { label: "Engaged", value: "36,104", delta: "+9%" },
          { label: "Key events", value: "1,214", delta: "+24%" },
        ].map((m) => (
          <div key={m.label}>
            <dt className="text-xs font-medium text-neutral-500">{m.label}</dt>
            <dd className="mt-1 font-display text-[28px] font-[900] leading-none tracking-[-0.02em] text-ink tabular-nums">
              {m.value}
            </dd>
            <p className="mt-0.5 text-[11px] font-bold text-green-700 tabular-nums">{m.delta}</p>
          </div>
        ))}
      </dl>
      <div className="grid grid-cols-[84px_1fr_56px] items-center gap-x-3.5 gap-y-2.5 text-[13px]">
        {[
          { label: "Instagram", pct: 92, val: "18,432" },
          { label: "LinkedIn", pct: 58, val: "9,881" },
          { label: "TikTok", pct: 42, val: "7,102" },
          { label: "Facebook", pct: 22, val: "3,842" },
        ].map((row) => (
          <div key={row.label} className="contents">
            <div className="font-medium text-neutral-700">{row.label}</div>
            <div
              aria-hidden="true"
              className="relative h-2 overflow-hidden rounded-full bg-neutral-100"
            >
              <span
                className="block h-full rounded-full bg-purple-500"
                style={{ width: `${row.pct}%` }}
              />
            </div>
            <div className="text-right font-bold text-ink tabular-nums">{row.val}</div>
          </div>
        ))}
      </div>
      <div className="absolute -bottom-5 -right-6 min-w-[160px] rounded-2xl border border-neutral-950/5 bg-white px-[18px] py-3.5 shadow-[0_18px_36px_-10px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
        <p className="font-mono text-[10px] uppercase tracking-[0.09em] text-neutral-500">
          UTM Health score
        </p>
        <p className="mt-1 font-display text-[44px] font-[900] leading-none tracking-[-0.025em] text-ink tabular-nums">
          82<span className="ml-0.5 text-base font-bold text-neutral-400">/100</span>
        </p>
      </div>
    </div>
  );
}

function Marquee() {
  return (
    <div className="border-y border-neutral-100 bg-white py-6">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-3.5 px-8 text-[13px] text-neutral-600">
        {/* SKE-3472 — logo is now a link to skedsocial.com (new tab) so
         *  brand attribution is clickable. Underline-offset + hover
         *  ring from the utility class kept minimal — the image sits
         *  as the anchor's visual affordance, no extra chrome needed. */}
        <a
          href={LANDING_MARQUEE_LOGO_HREF}
          target="_blank"
          rel="noreferrer"
          aria-label="Sked Social (opens in new tab)"
          className="inline-flex shrink-0 rounded-sm focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
        >
          <img
            src="/sked-logo.webp"
            alt="Sked Social"
            className="h-5 w-auto"
            width="92"
            height="20"
          />
        </a>
        <span>
          <em className="font-serif italic font-semibold tracking-[-0.005em] text-ink">
            Sked Social
          </em>{" "}
          is the leading social media management tool used by leading brands like Qantas, IHG and
          The Iconic.
        </span>
        <a
          href={LANDING_MARQUEE_LEARN_MORE_HREF}
          target="_blank"
          rel="noreferrer"
          className="ts-link ml-1 inline-flex items-center gap-1"
        >
          Learn more <span className="ts-link-arrow">→</span>
        </a>
      </div>
    </div>
  );
}

interface Cut {
  icon: React.ReactNode;
  title: string;
  body: string;
  tag: string;
  pink?: boolean;
}

const CUTS: Cut[] = [
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 4 6 4 9s-1.5 6.4-4 9c-2.5-2.6-4-6-4-9s1.5-6.4 4-9Z" />
      </svg>
    ),
    title: "Where your traffic actually comes from.",
    body: "Instagram, TikTok, LinkedIn, Facebook, X, YouTube, Pinterest, Threads — ranked by sessions, engaged users and key events. Plus a country- and city-level geography cut, filtered to social sessions only so bot traffic doesn't pollute the read.",
    tag: "01 · Sources",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
    title: "Which pages social actually converts.",
    body: "Landing-page winners, ranked by sessions-per-user from social traffic — not just which got the most clicks. Long URLs stay copyable.",
    tag: "02 · Content",
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 12h4l2-4 4 8 2-4h6" />
      </svg>
    ),
    title: "Whether your UTMs are helping or hurting.",
    body: "A score out of 100 with every deduction explained. Flagged rows exportable to CSV, so you can fix them in whatever tool published them.",
    tag: "03 · Attribution",
    pink: true,
  },
];

function ThreeReads() {
  return (
    <section id="cuts" className="py-24 md:py-28">
      <div className="mx-auto max-w-[1200px] px-8">
        {/* SKE-3472 — heading-group wrapper has no max-w (per the ui
         *  skill's heading-group rules — constrain each text element
         *  individually, never the wrapper). The h2 uses max-w-[30ch]
         *  for text-5xl per the same guidance, which breaks the
         *  heading into a balanced 2-line wrap instead of the cramped
         *  4-line wrap the previous max-w-[22ch] produced on desktop.
         *  `md:text-[48px]` dropped because text-5xl is already 48px. */}
        <div className="mb-12">
          <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
            Opinionated reporting
          </span>
          <h2 className="max-w-[30ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance">
            Three reads that answer what social people{" "}
            <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
              actually
            </span>{" "}
            get asked.
          </h2>
          <p className="mt-[18px] max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
            Every read is shaped for a social team's narrative — not a BI analyst's. Plain language
            for empty states, broken UTMs and platform quirks.
          </p>
        </div>
        <ul role="list" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CUTS.map((cut) => (
            <li
              key={cut.title}
              className="group flex flex-col gap-2.5 rounded-[18px] border border-neutral-100 bg-white p-7 transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-500/20 hover:shadow-[0_14px_34px_-16px_rgba(16,24,40,0.18)]"
            >
              <span
                className={
                  "mb-1.5 inline-flex size-10 items-center justify-center rounded-[10px] " +
                  (cut.pink ? "bg-pink-50 text-pink-600" : "bg-purple-50 text-purple-600")
                }
              >
                {cut.icon}
              </span>
              <h3 className="font-display text-xl font-bold tracking-[-0.012em] text-ink">
                {cut.title}
              </h3>
              <p className="text-sm leading-[1.55] text-neutral-600">{cut.body}</p>
              <div className="mt-auto border-t border-dashed border-neutral-100 pt-3.5 font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
                {cut.tag}
              </div>
            </li>
          ))}
        </ul>
        <p className="mx-auto mt-10 max-w-[56ch] text-center font-serif italic text-[17px] font-medium leading-[1.4] text-purple-700">
          That's the whole report. No dashboards with twelve tabs. If a read doesn't change what
          you'd do on Monday, we leave it out.
        </p>
      </div>
    </section>
  );
}

function UtmHealthStage() {
  return (
    <section
      id="health"
      className="py-24 md:py-28"
      style={{ background: "linear-gradient(180deg, #fff 0%, #F4F2FF 100%)" }}
    >
      <div className="mx-auto max-w-[1200px] px-8">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <div>
            <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
              Companion · UTM Health Snapshot
            </span>
            <h2 className="max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance md:text-[48px]">
              Find out whether your UTMs are{" "}
              <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
                helping
              </span>{" "}
              or hurting.
            </h2>
            <p className="mt-[18px] max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
              A score out of 100. Every deduction explained. Flagged rows downloadable as CSV. And a
              taxonomy builder seeded from your audit findings, so next campaign ships clean.
            </p>
            <Link to="/utm-health-check" className="ts-link mt-5 inline-flex items-center gap-1">
              Try the audit without signing in <span className="ts-link-arrow">→</span>
            </Link>
          </div>
          <UtmHealthDemoCard />
        </div>
      </div>
    </section>
  );
}

function UtmHealthDemoCard() {
  const issues = [
    { label: "Inconsistent source casing", n: 36, tone: "severe" as const },
    { label: "Missing utm_medium", n: 9, tone: "warn" as const },
    { label: "Non-canonical utm values", n: 7, tone: "warn" as const },
    { label: "Missing utm_content on carousels", n: 21, tone: "ok" as const },
  ];
  const toneDot: Record<(typeof issues)[number]["tone"], string> = {
    severe: "bg-red-500",
    warn: "bg-yellow-500",
    ok: "bg-green-500",
  };
  return (
    <div className="rounded-[20px] border border-neutral-950/5 bg-white p-7 shadow-[0_22px_44px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
      <div className="mb-2.5 flex items-baseline justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
            UTM Health · Last 90 days
          </p>
          <p className="mt-1 font-display text-lg font-bold tracking-[-0.008em] text-ink">
            Sked Social
          </p>
        </div>
      </div>
      <div className="mt-1.5 mb-3.5 flex items-baseline gap-2.5">
        <p className="font-display text-[72px] font-[900] leading-none tracking-[-0.03em] text-ink tabular-nums">
          68
        </p>
        <span className="rounded-full bg-yellow-50 px-2.5 py-1.5 text-[13px] font-bold text-yellow-700">
          Needs work
        </span>
      </div>
      <div aria-hidden="true" className="mb-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full w-[68%] rounded-full bg-gradient-to-r from-yellow-500 to-pink-500" />
      </div>
      <ul role="list" className="flex flex-col gap-3">
        {issues.map((i) => (
          <li key={i.label} className="flex items-center justify-between gap-3.5 text-[13.5px]">
            <div className="flex items-center gap-2.5 text-neutral-700">
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${toneDot[i.tone]}`}
              />
              {i.label}
            </div>
            <span className="text-xs font-semibold text-neutral-500 tabular-nums">{i.n} rows</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AboutSked() {
  return (
    <section id="about" className="bg-peach py-24 md:py-28">
      <div className="mx-auto max-w-[1200px] px-8">
        <div className="grid items-center gap-14 md:grid-cols-[1fr_1.1fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-purple-700">
              About Sked Social
            </p>
            {/* Aligned with the UtmHealthStage and FinalCtaBand H2
             *  cadence — Gotham display Black, purple Quarto italic
             *  highlight word, same `max-w` cap so the headline
             *  rhythm reads consistent across the three marketing
             *  bands. */}
            <h2 className="my-[18px] max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance md:text-[48px]">
              Built by the team that cares about social enough to build{" "}
              <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
                this
              </span>
              .
            </h2>
            <p className="mt-3.5 max-w-[52ch] text-base leading-[1.6] text-neutral-700">
              Sked Social is the connected workspace where growing social teams plan, approve,
              publish, listen, and report — used by 10,000+ marketers, agencies and in-house teams.
            </p>
            <p className="mt-3.5 max-w-[52ch] text-base leading-[1.6] text-neutral-700">
              One calendar for every network. Approvals your stakeholders can actually use. Reports
              your CMO will read without rewriting. And the UTM hygiene that makes a tool like
              Traffic Snapshot honest.
            </p>
            <p className="mt-6 max-w-[48ch] font-serif italic text-[17px] font-medium leading-[1.4] text-purple-700">
              Here you <em className="font-serif italic font-semibold">see</em> attribution. In Sked
              you <em className="font-serif italic font-semibold">do</em> the work.
            </p>
            <ul
              role="list"
              className="mt-3.5 flex flex-col gap-2 text-base leading-[1.5] text-neutral-700"
            >
              <li className="flex gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-[9px] size-1.5 shrink-0 rounded-full bg-pink-500"
                />
                <span>
                  <strong className="font-semibold text-ink">Plan, approve, publish</strong> —
                  across every network, from one calendar.
                </span>
              </li>
              <li className="flex gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-[9px] size-1.5 shrink-0 rounded-full bg-pink-500"
                />
                <span>
                  <strong className="font-semibold text-ink">Listen and respond</strong> — inbox,
                  mentions and competitor scans in one place.
                </span>
              </li>
              <li className="flex gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-[9px] size-1.5 shrink-0 rounded-full bg-pink-500"
                />
                <span>
                  <strong className="font-semibold text-ink">Report</strong> — branded,
                  stakeholder-ready, every Monday, automatic.
                </span>
              </li>
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a
                href={LANDING_MID_EXPLORE_HREF}
                target="_blank"
                rel="noreferrer"
                className="ts-cta"
              >
                Explore Sked Social <span className="ts-cta-arrow">→</span>
              </a>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[20px] shadow-[0_30px_60px_-20px_rgba(16,24,40,0.26)]">
            <span className="absolute left-3.5 top-3.5 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 pl-2 text-xs font-bold text-ink backdrop-blur">
              <SkedMark className="size-[22px]" aria-hidden="true" />
              The workspace behind Traffic Snapshot
            </span>
            <img
              src="/sked-dashboard.webp"
              alt="Sked Social dashboard — approvals, PDF/Excel reports, community management, listening on all plans."
              className="block w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCtaBand() {
  return (
    <section className="py-24 md:py-28">
      <div className="mx-auto max-w-[1200px] px-8">
        <div className="grid items-center gap-9 rounded-[28px] bg-[#0B1120] p-12 text-white shadow-[0_40px_80px_-30px_rgba(79,49,255,0.4)] md:grid-cols-[1.15fr_0.85fr] md:p-16">
          <div>
            <h2 className="max-w-[22ch] font-display text-[40px] font-[900] leading-[1.04] tracking-[-0.025em] text-balance md:text-[46px]">
              One minute from{" "}
              <span className="font-serif italic font-semibold text-pink-300">connect</span> to
              clarity.
            </h2>
            <p className="mt-3.5 max-w-[48ch] text-[17px] leading-[1.55] text-white/70">
              Bring your GA4. Leave with a report a CMO will forward and a UTM audit that pays for
              itself on the next campaign. Branded PDF, CSV and weekly-digest email included.
            </p>
            <div className="mt-[22px] flex flex-wrap items-center gap-[18px]">
              <Link to="/app" className="ts-cta ts-cta-lg">
                Get free report <span className="ts-cta-arrow">→</span>
              </Link>
              <a
                href={LANDING_FINAL_CTA_PRICING_HREF}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-purple-200 no-underline hover:text-purple-100"
              >
                See Sked pricing <span>→</span>
              </a>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <SkedMark
              className="h-56 w-56 drop-shadow-[0_28px_44px_rgba(79,49,255,0.1)] drop-shadow-[0_6px_14px_rgba(16,24,40,0.06)]"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="py-14 pb-20">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-6 px-8">
        <Link to="/" className="flex items-center gap-2.5 text-ink no-underline">
          <SkedMark className="size-7" aria-hidden="true" />
          <span className="font-display text-[15px] font-[900] tracking-[-0.01em] text-ink">
            Traffic Snapshot
          </span>
        </Link>
        <span className="text-[13px] text-neutral-500">
          <b className="font-bold text-ink">Traffic Snapshot</b> · a free report from Sked Social ·{" "}
          <i className="font-serif italic font-medium text-purple-700">social-first</i> analytics,
          built for marketers.
        </span>
        <div className="flex-1" />
        <a
          href="https://skedsocial.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Privacy
        </a>
        <a
          href="https://skedsocial.com/terms-of-service"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Terms
        </a>
        <a
          href={LANDING_SITE_FOOTER_HREF}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          skedsocial.com ↗
        </a>
      </div>
    </footer>
  );
}
